import {
  createInitialState,
  programmeDuChapitre,
  reglageCourant,
  simulateSemaine,
} from '@neopolis/engine';
import type { Content, GameState } from '@neopolis/engine';

import { T } from '../../textes-interface.js';

/**
 * La fiche technique du chapitre 3, celle qui part dans le cahier.
 *
 * Elle montre le programme tel que l'îlot l'a écrit — dans la même forme qu'à
 * l'écran de réparation, pour qu'il se reconnaisse — puis la comparaison avec
 * le programme de la ville. C'est cette comparaison qui donne sens au travail :
 * même parc, même semaine, et un tiers de l'eau.
 */
export function FicheArrosage({
  content,
  partie,
  onEcrireTrace,
  onFermer,
}: {
  content: Content;
  partie: GameState;
  onEcrireTrace: (fieldId: string, valeur: string) => void;
  onFermer: () => void;
}) {
  const chapitre = content.chapitres.find((c) => c.id === 'ch3');
  const programme = programmeDuChapitre(content, 'ch3');
  const reglage = reglageCourant(partie, content);

  const notre = simulateSemaine(partie, content);
  // La ville, c'est le programme de départ : un état vierge le porte déjà.
  const ville = simulateSemaine(
    { ...createInitialState(content, partie.islandCode), chapterId: 'ch3' },
    content,
  );

  const champ = (id: string) => chapitre?.trace_ecrite.champs.find((f) => f.id === id);
  const champCapteur = champ('role_capteur');
  const champMiseAuPoint = champ('mise_au_point');

  const grandeur = programme?.reglages.grandeurs.find((g) => g.id === reglage?.condition?.grandeur);
  const comparateur = programme?.reglages.comparateurs.find(
    (c) => c.id === reglage?.condition?.comparateur,
  );

  const economie = ville.litresTotal - notre.litresTotal;

  return (
    <article className="rounded-2xl border-2 border-slate-300 bg-white p-5 print:border-0 print:p-0">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{chapitre?.trace_ecrite.titre}</h2>
          <p className="text-sm text-slate-600">
            {T.rapport.ilot} {partie.islandCode}
          </p>
        </div>

        <div className="flex gap-2 print:hidden">
          <button
            type="button"
            onClick={() => globalThis.print()}
            className="min-h-touch rounded-xl bg-sky-600 px-4 font-semibold text-white hover:bg-sky-700"
          >
            {T.rapport.imprimer}
          </button>
          <button
            type="button"
            onClick={onFermer}
            className="min-h-touch rounded-xl bg-slate-100 px-4 font-semibold text-slate-700"
          >
            {T.rapport.fermer}
          </button>
        </div>
      </header>

      {reglage && (
        <section className="mt-5">
          <h3 className="font-bold text-slate-900">{T.fiche3.programme}</h3>
          <div className="mt-2 rounded-xl border-2 border-slate-200 bg-slate-50 p-3 font-mono leading-relaxed text-slate-900">
            <p className="font-bold">{T.reparer.chaqueJour}</p>
            <p className="pl-4">
              <span aria-hidden="true">├─ </span>
              {T.reparer.a} {String(reglage.heure).padStart(2, '0')} {T.reparer.uniteHeure}
            </p>
            {reglage.condition && grandeur && comparateur && (
              <p className="pl-4">
                <span aria-hidden="true">├─ </span>
                {T.reparer.si} {grandeur.nom} {comparateur.nom} {reglage.condition.seuil}{' '}
                {grandeur.unite}
              </p>
            )}
            <p className="pl-4">
              <span aria-hidden="true">└─ </span>
              {reglage.condition ? `${T.reparer.alors} ` : ''}
              {T.reparer.arroserPendant} {reglage.duree_minutes} {T.reparer.uniteMinutes}
            </p>
          </div>
        </section>
      )}

      {champCapteur && (
        <section className="mt-5">
          <label htmlFor="trace-capteur" className="block font-bold text-slate-900">
            {champCapteur.label}
          </label>
          <textarea
            id="trace-capteur"
            rows={2}
            value={partie.writtenTrace.ch3['role_capteur'] ?? ''}
            onChange={(evenement) => onEcrireTrace('role_capteur', evenement.target.value)}
            className="mt-1 w-full rounded-xl border-2 border-slate-300 p-3 focus:border-sky-600 print:border-0 print:p-0"
          />
        </section>
      )}

      <section className="mt-5">
        <h3 className="font-bold text-slate-900">{T.fiche3.bilan}</h3>
        <dl className="mt-2 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl bg-slate-100 p-3">
            <dt className="text-sm text-slate-600">{T.fiche3.eauAvant}</dt>
            <dd className="text-xl font-bold tabular-nums text-slate-900">
              {Math.round(ville.litresTotal)} {T.semaine.litres}
            </dd>
            <dd className="text-sm text-slate-600">
              {ville.jours.filter((j) => j.arrose).length} {T.fiche3.arrosages}
            </dd>
          </div>
          <div className="rounded-xl bg-sky-50 p-3">
            <dt className="text-sm text-slate-600">{T.fiche3.eauApres}</dt>
            <dd className="text-xl font-bold tabular-nums text-sky-900">
              {Math.round(notre.litresTotal)} {T.semaine.litres}
            </dd>
            <dd className="text-sm text-slate-600">
              {notre.jours.filter((j) => j.arrose).length} {T.fiche3.arrosages}
            </dd>
          </div>
          <div className="rounded-xl bg-emerald-50 p-3">
            <dt className="text-sm text-slate-600">{T.fiche3.economie}</dt>
            <dd className="text-xl font-bold tabular-nums text-emerald-800">
              {Math.round(economie)} {T.semaine.litres}
            </dd>
            <dd className="text-sm text-slate-600">
              {T.semaine.bilanSouffrance} : {notre.joursEnSouffrance}
            </dd>
          </div>
        </dl>
      </section>

      {champMiseAuPoint && (
        <section className="mt-5">
          <label htmlFor="trace-mise-au-point" className="block font-bold text-slate-900">
            {champMiseAuPoint.label}
          </label>
          <textarea
            id="trace-mise-au-point"
            rows={4}
            value={partie.writtenTrace.ch3['mise_au_point'] ?? ''}
            onChange={(evenement) => onEcrireTrace('mise_au_point', evenement.target.value)}
            className="mt-1 w-full rounded-xl border-2 border-slate-300 p-3 focus:border-sky-600 print:border-0 print:p-0"
          />
        </section>
      )}

      <section className="mt-5 rounded-xl bg-amber-50 p-3">
        <h3 className="font-bold text-slate-900">{T.rapport.aRetenir}</h3>
        <p className="mt-1 text-slate-800">{chapitre?.trace_ecrite.a_retenir}</p>
      </section>
    </article>
  );
}
